/**
 * Dashboard API Client — Phase 6A endpoints.
 *
 * Each function maps to a single backend endpoint that returns
 * a pre-computed, display-ready payload. No client-side computation.
 */

import axios from '@/lib/http';
import { getAuthToken } from '../utils/api';
import { getAuthHeaders } from '../lib/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Authenticated GET using the global Clerk token getter from utils/api */
async function authGet(path: string) {
  const headers: Record<string, string> = {};
  const token = await getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return axios.get(`${BACKEND_URL}${path}`, { headers });
}

/** Authenticated POST using the global Clerk token getter from utils/api */
async function authPost(path: string, body: unknown) {
  const headers: Record<string, string> = {};
  const token = await getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return axios.post(`${BACKEND_URL}${path}`, body, { headers });
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PipelineStage {
  status: 'complete' | 'in_progress' | 'waiting' | 'not_started' | 'error';
  completed_at?: string;
  sent_at?: string;
  opened_at?: string;
  reason?: string;
  received?: number;
  required?: number;
  parsed?: number;
  errors?: number;
  items?: DocumentItem[];
}

export interface DocumentItem {
  type: string;
  label: string;
  status: 'received' | 'missing' | 'parsing' | 'error';
  filename?: string;
  requested_at?: string;
}

export interface AIActivityEvent {
  timestamp: string;
  action: string;
  detail: string;
}

export interface CasePipelineStatus {
  case_id: string;
  client_name: string;
  status: string;
  status_label: string;
  days_open: number;
  pipeline: Record<string, PipelineStage>;
  ai_activity: AIActivityEvent[];
  intervention_needed: boolean;
  suggestions: string[];
}

export interface AccountantPayload {
  case_summary: {
    client_name: string;
    claim_amount_formatted: string;
    claim_amount: number;
    status: string;
    status_label: string;
    days_open: number;
    accounting_basis: string;
  };
  data_quality: {
    score: number;
    grade: string;
    missing_documents: string[];
    categories_reconciled: number;
    categories_total: number;
    variance_flags: number;
    last_updated: string;
  };
  forensic_insights: Array<{
    title: string;
    severity: 'high' | 'medium' | 'low';
    type: string;
    detail: string;
  }>;
  engine_outputs: Record<string, number | string>;
  next_actions: Array<{
    label: string;
    priority: 'high' | 'medium' | 'low';
    route: string;
  }>;
  war_room_summary: {
    total_issues: number;
    disputed: number;
    resolved: number;
    case_strength: number;
  };
  sign_off_ready: boolean;
  sign_off_blockers: string[];
}

export interface AttorneyCase {
  case_id: string;
  client_name: string;
  claim_amount_formatted: string;
  claim_amount: number;
  case_strength: number;
  case_strength_label: string | null;
  status: string;
  status_label: string;
  days_open: number;
  next_action: string;
  settlement_position: {
    demand: number;
    demand_formatted: string;
    carrier_offer: number;
    carrier_offer_formatted: string;
    gap: number;
    gap_formatted: string;
    gap_pct: number;
  } | null;
  war_room: {
    disputed_issues: number;
    case_strength: number;
  };
  deliverables: Record<string, 'ready' | 'in_progress' | 'not_started'>;
}

export interface AttorneyPayload {
  cases: AttorneyCase[];
  portfolio_summary: {
    total_cases: number;
    total_claim_value_formatted: string;
    total_claim_value: number;
    cases_ready_for_demand: number;
    cases_in_review: number;
    cases_pending_docs: number;
  };
}

export interface AgentSummary {
  agent_name: string;
  display_name: string;
  events_count: number;
  tools_used: string[];
  confidence: number;
  position_value: number;
  position_value_formatted: string;
  key_finding: string;
  resolution_paths_count: number;
}

export interface AgentSummariesPayload {
  agents: AgentSummary[];
  summary: {
    total_events: number;
    total_tools_used: number;
    disagreements_found: number;
    consensus_reached: number;
  };
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/** 6A.1 — Case pipeline status for Case Status View */
export async function getCasePipelineStatus(claimId: string): Promise<CasePipelineStatus> {
  const { data } = await authGet(`/api/v1/case-pipeline/${claimId}/pipeline-status`);
  return data;
}

/** 6A.2 — Accountant dashboard payload (single claim) */
export async function getAccountantPayload(claimId: string): Promise<AccountantPayload> {
  const { data } = await authGet(`/api/v1/dashboard/accountant-payload/${claimId}`);
  return data;
}

/** 6A.3 — Attorney dashboard payload (all cases for user) */
export async function getAttorneyPayload(userId: string): Promise<AttorneyPayload> {
  const { data } = await authGet(`/api/v1/dashboard/attorney-payload/${userId}`);
  return data;
}

/**
 * 6E — Attorney portfolio dashboard payload (all cases for the authenticated
 * attorney). Unlike `getAttorneyPayload`, this authenticates with the attorney
 * JWT (localStorage `attorney_token`), falling back to the Clerk token via
 * `getAuthHeaders()`, and the endpoint scopes by the token itself — there is
 * no userId in the path.
 *
 * Returns `{ cases: [], portfolio_summary: {...zeros...} }` (not a 404) for an
 * attorney with no claims.
 */
export async function getAttorneyPortfolio(): Promise<AttorneyPayload> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${BACKEND_URL}/api/v1/attorney/portfolio`, {
    headers: authHeaders,
  });
  if (!response.ok) {
    throw new Error(`Failed to load attorney portfolio (${response.status})`);
  }
  return response.json();
}

/** 6A.5 — War Room agent summaries */
export async function getAgentSummaries(reportId: string): Promise<AgentSummariesPayload> {
  const { data } = await authGet(`/api/v1/deliberation/${reportId}/agent-summaries`);
  return data;
}

/** Download WB1 workbook */
export function downloadWB1(reportId: string): string {
  return `/api/v1/workbook/wb1/${reportId}`;
}

/** Download WB2 workbook */
export function downloadWB2(reportId: string): string {
  return `/api/v1/workbook/wb2/${reportId}`;
}

// ---------------------------------------------------------------------------
// Pipeline API
// ---------------------------------------------------------------------------

export interface PipelineRunStatus {
  report_id: string;
  status: 'running' | 'completed' | 'completed_with_errors' | 'failed' | 'no_runs';
  trigger_type?: string;
  stages: Record<string, {
    status: 'not_started' | 'running' | 'completed' | 'failed' | 'retrying' | 'skipped';
    started_at?: string;
    completed_at?: string;
    error?: string;
    retry_count?: number;
    result_summary?: Record<string, unknown>;
  }>;
  created_at?: string;
  updated_at?: string;
}

export async function startPipeline(reportId: string): Promise<{ status: string; task_id: string; report_id: string }> {
  const { data } = await authPost(`/api/v1/pipeline/${reportId}/start`, { trigger_type: 'manual' });
  return data;
}

export async function getPipelineRunStatus(reportId: string): Promise<PipelineRunStatus> {
  const { data } = await authGet(`/api/v1/pipeline/${reportId}/run-status`);
  return data;
}

export async function retryPipelineStage(reportId: string, stage: string): Promise<{ status: string }> {
  const { data } = await authPost(`/api/v1/pipeline/${reportId}/retry/${stage}`, {});
  return data;
}
